// The one input shape used by every prompt on the SyQi dashboard: a bare
// coloured field plus a send glyph. No wrapper card, no Submit pill — the
// prompt text above it is plain text owned by the caller.
//
// The field's fill IS its status light. Empty means "you still owe this an
// answer", so it pulses red; the moment there's text in it, it goes green.
//
// The pulse is an OPACITY fade on a deep-red overlay, not an interpolated
// backgroundColor. Colour interpolation was measured twice on web as advancing
// a single leg and then freezing (Animated.loop(sequence) never moved at all);
// opacity is native-drivable and loops reliably.
//
// Used by ClarifyBox (SyQi's clarifying question) and ClioWidget (journal
// entry). Keep both on this component so the two never drift apart.
import React, { useEffect, useRef } from 'react';
import { ActivityIndicator, Animated, Easing, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Send } from 'lucide-react-native';
import { useTheme, makeThemedStyles } from '../../v10/theme';

export function PromptField({
  value,
  onChangeText,
  onSubmit,
  placeholder,
  maxLength = 400,
  minHeight = 38,
  submitting = false,
  accessory,
}: {
  value: string;
  onChangeText: (next: string) => void;
  onSubmit: () => void;
  placeholder: string;
  maxLength?: number;
  /** Tall for CLIO's journal entry, short for a one-line clarify answer. */
  minHeight?: number;
  submitting?: boolean;
  /** Optional control on the field's own row, left of the send glyph (CLIO's
   *  journal-type dropdown). */
  accessory?: React.ReactNode;
}) {
  const { c, t } = useTheme();
  const styles = useStyles();
  const empty = !value.trim();
  const canSubmit = !submitting && !empty;

  // 0 → plain red, 1 → deep red. Only ever driven while the field is empty.
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!empty) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1000,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1000,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => {
      loop.stop();
      pulse.setValue(0);
    };
  }, [empty, pulse]);

  return (
    <View style={styles.row}>
      <View
        style={[
          styles.field,
          { minHeight },
          empty ? styles.fieldEmpty : styles.fieldFilled,
        ]}
      >
        {/* Deep-red wash faded in and out over the base red. Sits behind the
            text (the TextInput follows it in paint order) and never intercepts
            touches. */}
        {empty && (
          <Animated.View
            pointerEvents="none"
            style={[StyleSheet.absoluteFill, styles.pulseWash, { opacity: pulse }]}
          />
        )}
        <TextInput
          style={[styles.input, webNoFocusRing, { minHeight: minHeight - 18 }]}
          placeholder={placeholder}
          placeholderTextColor={c.inputPlaceholderOnFill}
          value={value}
          onChangeText={onChangeText}
          multiline
          maxLength={maxLength}
          editable={!submitting}
        />
      </View>
      {accessory}
      <Pressable
        style={[styles.send, !canSubmit && styles.sendDisabled]}
        onPress={onSubmit}
        disabled={!canSubmit}
        hitSlop={10}
      >
        {submitting ? (
          <ActivityIndicator size="small" color={t.gold} />
        ) : (
          <Send size={28} color={t.gold} strokeWidth={2.25} />
        )}
      </Pressable>
    </View>
  );
}

// RN-web draws its own focus ring inside the field, which reads as a second
// border against our 2px rim. The rim already shows focus well enough.
const webNoFocusRing = Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : null;

const useStyles = makeThemedStyles(({ c, radii }) =>
  StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-end', gap: 10 },
  field: {
    flex: 1,
    borderRadius: radii.tile,
    borderWidth: 2,
    paddingHorizontal: 12,
    paddingVertical: 8,
    justifyContent: 'flex-start',
    overflow: 'hidden', // keeps the pulse wash inside the rounded corners
  },
  fieldEmpty: {
    backgroundColor: c.inputFillEmpty,
    borderColor: c.inputRimEmpty,
  },
  fieldFilled: {
    backgroundColor: c.inputFillFilled,
    borderColor: c.inputRimFilled,
  },
  pulseWash: { backgroundColor: c.inputFillEmptyPulse },
  input: {
    fontSize: 13,
    color: c.text,
    textAlignVertical: 'top',
    padding: 0,
  },
  send: { paddingBottom: 4 },
  sendDisabled: { opacity: 0.35 },
  }),
);

export default PromptField;
